<!-- **** --------------------------------------------------------------------------
	SHARED SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts">
    import { JourneyTextNode } from '@journeys/Journeys.viewmodel';
    import vm from '@journeys/Journeys.viewmodel';
    import { onMounted, ref, watch, WatchStopHandle, onUnmounted, reactive, computed, nextTick, onBeforeUnmount, Ref } from 'vue';

    import { ColorProp } from '@journeys/Enums';
    import { thresholdCallListener, uid } from '@shared/utils';
    import { usePointer, UsePointerProps } from '@shared/utils/UseEvent';

    import { TextNodeVariation, TextNodeAlign } from '@journeys/Enums';

    const nodeMap: WeakMap<Element, (rect: DOMRect) => void> = new WeakMap();
    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (let { target, contentRect } of entries) {
            let cb = nodeMap.get(target);
            cb && cb(contentRect);
        }
    });

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Journey_TextNode',
    };
    export default {
        name: component.name,
    };
</script>

<!-- **** --------------------------------------------------------------------------
	SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    const cid = uid();

    const { settings, editor, nav } = vm,
        { p_mod } = settings.canvas,
        props = withDefaults(
            defineProps<{
                node: JourneyTextNode;
                moveNode?: Func;
            }>(),
            {
                moveNode: (ev: any, node: any) => {},
            }
        ),
        state = reactive({
            edit: false,
        }),
        fontColor = computed(() => {
            const [h, s, l, a = 100] = props.node.fc;
            return `hsl(${h}, ${s}%, ${l}%, ${a}%)`;
        }),
        doubleClick = thresholdCallListener(settings.canvas.doubleClickThreshold);

    let nodeRef: Ref<HTMLElement | null> = ref(null);

    const getRuntimeDims = () => {
        props.node.runtime!.h = nodeRef.value!.offsetHeight;
        props.node.runtime!.w = nodeRef.value!.offsetWidth;
    };

    onMounted(() => {
        props.node.runtime ||= {} as JourneyTextNode['runtime'];
        if (editor.tools.sel_count <= 1) props.node.runtime!.el = nodeRef.value!;
        getRuntimeDims();

        nodeMap.set(nodeRef.value!, (rect) => {
            let node = props.node,
                h = rect.height,
                w = rect.width;

            if (!node.h)
                if (node.runtime!.h !== h) node.runtime!.h = h;
                else if (node.h !== h)
                    if (!node.w)
                        if (node.runtime!.w !== w) node.runtime!.w = w;
                        else if (node.w !== w) node.w = w;
        });
    });

    onBeforeUnmount(() => {
        resizeObserver.unobserve(nodeRef.value!);
    });

    const handleBlur = () => {
        state.edit = false;
        resizeObserver.unobserve(nodeRef.value!);
    };

    const handlePointerDown = (ev: PointerEvent, node: JourneyTextNode) => {
        if (editor.state.activeTool === 'text' || (editor.state.activeTool === 'pointer' && doubleClick())) {
            state.edit = true;
            resizeObserver.observe(nodeRef.value!);

            editor.events.emit('clearselection');
            editor.tools.sel_keyObj = node.id;
            editor.tools.sel_nodes[node.id] = node;
        } else if (!state.edit) props.moveNode(ev, node);
    };

    const handleResize = (handle: string, ev: PointerEvent) => {
        getRuntimeDims();

        let node = props.node,
            x = props.node.x,
            w = node.w || node.runtime?.w || 0;

        usePointer.move(({ offset }: UsePointerProps) => {
            switch (handle) {
                case 'right':
                    node.w = ~~(w + offset.x * p_mod.value);
                    break;
                case 'left':
                    node.x = ~~(x + offset.x * p_mod.value);
                    node.w = ~~(w - offset.x * p_mod.value);
                    break;
            }
        }, ev);
    };

    const handleResetSize = async () => {
        props.node.w = 0;

        await nextTick();

        getRuntimeDims();
    };
</script>

<!-- **** --------------------------------------------------------------------------
	TEMPLATE
-------------------------------------------------------------------------- **** -->
<template>
    <div
        :key="`jn${node.id}`"
        :id="`jn${node.id}`"
        class="text-node"
        :data-id="node.id"
        data-nodetype="canvas-text"
        :style="`
         width: ${node.w === 0 ? 'max-content' : node.w + 'px'};
         transform: translate3d(${node.x}px, ${node.y}px, 0)`"
        @pointerdown.left="(ev: any) => handlePointerDown(ev, node)">
        <div
            class="text-node-content"
            ref="nodeRef"
            :contenteditable="state.edit"
            :style="{
                color: fontColor,
                fontSize: node.fs + 'px',
                fontWeight: node.fw,
                fontStyle: node.fv === TextNodeVariation.Italic ? 'italic' : 'normal',
                textAlign: node.fa === TextNodeAlign.Left ? 'left' : node.fa === TextNodeAlign.Center ? 'center' : 'right',
            }"
            @change="({ target }: Event) => (node.data.text = (target as HTMLElement).innerText)"
            @blur="handleBlur"
            v-html="node.data.text"></div>
        <div class="text-node-ui" v-if="(editor.tools.sel_count === 1 && editor.tools.sel_nodes[node.id]) || editor.tools.sel_keyObj === node.id || state.edit">
            <!-- <template v-if="!state.edit"> -->
            <div class="drag-handle horizontal left" @pointerdown.left.stop="(ev: PointerEvent) => handleResize('left', ev)"></div>
            <div class="drag-handle horizontal right" @pointerdown.left.stop="(ev: PointerEvent) => handleResize('right', ev)"></div>
            <div class="reset-handle left" v-if="node.w" @pointerdown.left.stop="handleResetSize">a</div>
            <!-- </template> -->
        </div>
    </div>
</template>

<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    .reset-handle {
        --handle-size: calc(20px * var(--dist-m));
        --handle-offset: calc(var(--handle-size) * -0.5 - var(--outline-size) / 2);
        position: absolute;
        display: block;
        text-align: center;
        font-weight: 700;
        font-size: calc(14px * var(--dist-m));
        height: var(--handle-size);
        width: var(--handle-size);
        background-color: #f7f7f7;
        cursor: pointer;

        opacity: 0.6;
        transition: opacity 0.1s ease;

        pointer-events: all;
        z-index: 4;
    }
    .reset-handle.left {
        top: 0;
        bottom: 0;
        margin: auto 0;
        right: calc(var(--handle-offset) + var(--handle-size));
    }
    .reset-handle:hover {
        opacity: 1;
    }

    .text-node {
        --ui-offset: 6px;
        position: absolute;
        height: auto;
        pointer-events: auto;

        will-change: width, transform;
    }

    .text-node-content:focus-visible {
        outline: none;
    }

    .text-node-ui {
        --outline-size: calc(2px * var(--dist-m));
        display: block;
        position: absolute;
        height: calc(100% + var(--ui-offset) * 2);
        width: calc(100% + var(--ui-offset) * 4);
        top: calc(var(--ui-offset) * -1);
        left: calc(var(--ui-offset) * -2);
        outline: var(--outline-size) solid #1fa9b07b;
        border-radius: 2px;

        pointer-events: none;

        will-change: width;
    }

    .text-node-ui .drag-handle {
        --handle-size: calc(10px * var(--dist-m));
        --handle-offset: calc(var(--handle-size) * -0.5 - var(--outline-size) / 2);
        display: block;
        position: absolute;
        height: var(--handle-size);
        width: var(--handle-size);
        border-radius: 2px;

        background-color: purple;

        pointer-events: all;
    }

    .text-node-ui .drag-handle.horizontal {
        top: 0;
        bottom: 0;
        margin: auto 0;

        cursor: e-resize;
    }
    .text-node-ui .drag-handle.left {
        left: var(--handle-offset);
    }
    .text-node-ui .drag-handle.right {
        right: var(--handle-offset);
    }
</style>
